/**
 * /operations/leave/encashment — Session 3a PR 6.
 *
 * Bulk leave-encashment runs (annual cycle) + individual encashment
 * requests. Rawatib-owned per Session 3a §4.5 — money calc + WPS
 * settlement responsibility lives with the payroll agent.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail (Rawatib narration)
 *   2. InlineAISuggestion cards (year-end forecast + Article 109 floor)
 *   3. TraceableValue wrapping amount + days_total + per-employee payout
 *      (≥3 wrappers)
 *
 * Page structure: TWO tabs (Active Runs · History). Active Runs is the
 * default landing.
 */

import { useQuery } from "@tanstack/react-query";
import { CalendarRange, Plus, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ErrorPanel, Loading } from "../../../components/Loading";
import {
  EntityDrawer,
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../../../components/ui";
import {
  session3aApi,
  type EncashmentRunItem,
} from "../../../lib/session3a-api";
import { useLegalEntityGate } from "../../../lib/legalEntity";
import { AiBriefPanel } from "../../../shell/AiBriefPanel";
import { formatSar, severityChip, showAlertTrace } from "../../_session3aHelpers";

type Tab = "active" | "history";

function NewRunDrawer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [scope, setScope] = useState<"all" | "department" | "grade" | "manual">("all");
  const [daysPolicy, setDaysPolicy] = useState<"full" | "above_floor" | "custom">(
    "above_floor",
  );

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={t(
        "operations.leave_encashment.new_run.title",
        `New encashment run · Step ${step} of 5`,
      )}
      footer={
        <div className="flex justify-between gap-2">
          <button
            type="button"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken disabled:opacity-40"
          >
            {t("common.back", "Back")}
          </button>
          {step < 5 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              className="rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
            >
              {t("common.next", "Next")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                showAlertTrace(
                  t(
                    "operations.leave_encashment.new_run.initiated",
                    "Run drafted. Routes through M15.5 chain with workflow_type='encashment'. The drafted run appears in the Active Runs tab once the POST endpoint is connected (separate workstream).",
                  ),
                );
                onClose();
              }}
              className="rounded-md bg-gradient-to-br from-[#00D3C2] to-[#2260C9] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
            >
              {t("operations.leave_encashment.new_run.initiate", "Initiate run")}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4 p-4 text-sm">
        {step === 1 && (
          <div>
            <h3 className="mb-2 font-semibold text-text-primary">
              {t("operations.leave_encashment.step1", "Step 1: Scope")}
            </h3>
            <div className="rounded-lg border border-[#e9d5ff] bg-[#f5f3ff] px-3 py-2 text-xs text-[#6b21a8] mb-3">
              ✦{" "}
              {t(
                "operations.leave_encashment.scope_hint",
                "Tashghil suggests: 28 employees exceed 30-day balance — run encashment for those.",
              )}
            </div>
            <div className="space-y-2">
              {[
                { value: "all", label: "All employees" },
                { value: "department", label: "By department" },
                { value: "grade", label: "By grade" },
                { value: "manual", label: "Manual selection" },
              ].map((opt) => (
                <label key={opt.value} className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="scope"
                    value={opt.value}
                    checked={scope === opt.value}
                    onChange={(e) => setScope(e.target.value as typeof scope)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}
        {step === 2 && (
          <div>
            <h3 className="mb-2 font-semibold text-text-primary">
              {t("operations.leave_encashment.step2", "Step 2: Days policy")}
            </h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="days_policy"
                  checked={daysPolicy === "full"}
                  onChange={() => setDaysPolicy("full")}
                />
                <span>{t("operations.leave_encashment.days_policy.full", "Encash full balance")}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="days_policy"
                  checked={daysPolicy === "above_floor"}
                  onChange={() => setDaysPolicy("above_floor")}
                />
                <span>{t("operations.leave_encashment.days_policy.above_floor", "Encash above Article 109 floor (recommended)")}</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="days_policy"
                  checked={daysPolicy === "custom"}
                  onChange={() => setDaysPolicy("custom")}
                />
                <span>{t("operations.leave_encashment.days_policy.custom", "Encash custom days")}</span>
              </label>
            </div>
          </div>
        )}
        {step === 3 && (
          <div>
            <h3 className="mb-2 font-semibold text-text-primary">
              {t("operations.leave_encashment.step3", "Step 3: Calculation preview")}
            </h3>
            <p className="text-xs text-text-secondary">
              {t(
                "operations.leave_encashment.step3_note",
                "Rawatib runs the DET money math: days × (basic_salary / 30). Per-employee breakdown surfaces with TraceableValue links when wiring lands.",
              )}
            </p>
          </div>
        )}
        {step === 4 && (
          <div>
            <h3 className="mb-2 font-semibold text-text-primary">
              {t("operations.leave_encashment.step4", "Step 4: Approval routing")}
            </h3>
            <p className="text-xs text-text-secondary">
              {t(
                "operations.leave_encashment.step4_note",
                "Uses M15.5 chain with workflow_type='encashment'. Chain resolved fresh at routing time (commandment 20).",
              )}
            </p>
          </div>
        )}
        {step === 5 && (
          <div>
            <h3 className="mb-2 font-semibold text-text-primary">
              {t("operations.leave_encashment.step5", "Step 5: Confirm")}
            </h3>
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
              {t(
                "operations.leave_encashment.step5_warn",
                "Initiating creates the run in draft state. Approval chain then routes for HR sign-off; nothing pays out until terminal approve.",
              )}
            </div>
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

export function LeaveEncashmentPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [tab, setTab] = useState<Tab>("active");
  const [showDrawer, setShowDrawer] = useState(false);

  const list = useQuery({
    queryKey: ["leave-encashment", legalEntityId, tab],
    queryFn: () => session3aApi.listEncashmentRuns(legalEntityId ?? ""),
    enabled: !!legalEntityId,
  });

  const items: EncashmentRunItem[] = list.data?.items ?? [];

  const stats = useMemo(() => {
    const pending = items.filter((r) =>
      ["draft", "calculated"].includes(r.status),
    );
    const sumPending = pending.reduce((acc, r) => acc + r.total_amount_sar, 0);
    return {
      total: items.length,
      pending: pending.length,
      sumPending,
      paidLastQuarter: items.filter((r) => r.status === "paid").length,
    };
  }, [items]);

  const visible = useMemo(
    () =>
      tab === "active"
        ? items.filter((r) => r.status !== "paid")
        : items.filter((r) => r.status === "paid"),
    [items, tab],
  );

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("operations.leave_encashment.title", "Leave Encashment")}
          subtitle={t(
            "operations.leave_encashment.subtitle",
            "Bulk encashment runs + individual encashment requests. Rawatib owns the money math and WPS settlement path.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => setTab("active")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                tab === "active"
                  ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.leave_encashment.tab.active", "Active runs")}
              {stats.pending > 0 && (
                <span className="ms-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-brand-blue px-1 text-[9px] font-semibold text-white">
                  {stats.pending}
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => setTab("history")}
              className={`rounded-full border px-3 py-1 text-xs font-medium ${
                tab === "history"
                  ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                  : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
              }`}
            >
              {t("operations.leave_encashment.tab.history", "Encashment history")}
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowDrawer(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("operations.leave_encashment.cta.new_run", "New encashment run")}
          </button>
        </div>

        <InlineAISuggestion
          agent="rawatib"
          icon={<CalendarRange className="h-4 w-4" />}
          severity="info"
          headline={t(
            "operations.leave_encashment.suggestion.forecast_headline",
            "Year-end accrual forecast.",
          )}
          body={t(
            "operations.leave_encashment.suggestion.forecast_body",
            "At current accrual rates, several employees will hit max balance by Dec 31. Rawatib recommends scheduling the Q4 cycle.",
          )}
          primaryAction={{
            label: t(
              "operations.leave_encashment.suggestion.plan_q4",
              "Plan Q4 cycle",
            ),
            onClick: () => setShowDrawer(true),
          }}
          suggestionKey="leave-encashment-forecast"
        />
        <InlineAISuggestion
          agent="mutabaa"
          icon={<ShieldCheck className="h-4 w-4" />}
          severity="warning"
          headline={t(
            "operations.leave_encashment.suggestion.floor_headline",
            "Mutaba'aa note: Article 109 prohibits encashment below 21 days.",
          )}
          body={t(
            "operations.leave_encashment.suggestion.floor_body",
            "The encashment policy 'Encash above Article 109 floor' (default) protects every employee at the floor.",
          )}
          primaryAction={{
            label: t(
              "operations.leave_encashment.suggestion.open_policy",
              "Open policy",
            ),
            onClick: () =>
              window.location.assign("/policies/leave/encashment-rules"),
          }}
          suggestionKey="leave-encashment-floor"
        />

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {visible.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">
                    {t("operations.leave_encashment.col.run_id", "Run ID")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_encashment.col.initiated_by", "Initiated by")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_encashment.col.status", "Status")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_encashment.col.employees", "Employees")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_encashment.col.days_total", "Days total")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_encashment.col.amount", "Amount (SAR)")}
                  </th>
                  <th className="px-3 py-2">
                    {t("operations.leave_encashment.col.last_action", "Last action")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((row) => (
                  <tr
                    key={row.run_id}
                    className="border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() =>
                          showAlertTrace(
                            t(
                              "operations.leave_encashment.trace.run_detail",
                              `Run ${row.run_id} carries the per-employee line items. Drawer will show payouts when wiring lands.`,
                            ),
                          )
                        }
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.run_id.slice(0, 8)}…
                      </button>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.initiated_by_name}</td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${severityChip(
                          row.status === "paid"
                            ? "info"
                            : row.status === "approved"
                              ? "info"
                              : "neutral",
                        )}`}
                      >
                        {row.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">{row.employees_count}</td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.leave_encashment.trace.days_total",
                              `total_days = sum across ${row.employees_count} employees of days_to_encash. Policy 'above_floor' subtracts Article 109's 21-day floor first.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr">{row.total_days}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.leave_encashment.trace.amount",
                              `amount = sum(days_to_encash × basic_salary / 30). Each employee's value individually traceable in the run drawer.`,
                            ),
                          )
                        }
                      >
                        <span dir="ltr" className="font-medium text-text-primary">
                          {formatSar(row.total_amount_sar)}
                        </span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2 text-xs text-text-secondary">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "operations.leave_encashment.trace.last_action",
                              `${row.last_action_label}. Full workflow_event audit log in the drawer.`,
                            ),
                          )
                        }
                      >
                        <span>{row.last_action_label}</span>
                      </TraceableValue>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!list.isLoading && visible.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border-subtle p-6 text-center text-sm text-text-tertiary">
            {tab === "active"
              ? t(
                  "operations.leave_encashment.empty_active",
                  "No active encashment runs. ✦ Rawatib suggests starting the Q4 annual cycle now.",
                )
              : t(
                  "operations.leave_encashment.empty_history",
                  "No paid runs in history yet.",
                )}
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="rawatib"
          liveContext={{
            total: stats.total,
            attention: stats.pending,
            critical: 0,
            top_kind: t("operations.leave_encashment.title", "Leave Encashment"),
            window_label: tab === "active" ? "active" : "history",
          }}
        />
      </aside>
      {showDrawer && <NewRunDrawer onClose={() => setShowDrawer(false)} />}
    </div>
  );
}
